import './ConfirmModal.scss';
import PropTypes from 'prop-types';
import { useEffect, useRef, useState } from 'react';
import Error from '../../../../common/Error/Error';
import GoodsIn from '../../../../api/controllers/GoodsIn/GoodsIn';
import LoadingSpinner from '../../../../common/LoadingSpinner/LoadingSpinner';
import GoodsOut from '../../../../api/controllers/GoodsOut/GoodsOut';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWarning } from '@fortawesome/free-solid-svg-icons';

// General ConfirmModal Component
const ConfirmModal = ({
  category,
  id,
  movementType,
  setModalOpen,
  refreshMovement,
  damaged,
  packages,
  expectedAssignments,
}) => {
  const [error, setError] = useState();
  const [loading, setLoading] = useState();
  const [driverName, setDriverName] = useState();
  const canvasRef = useRef(null);
  const contextRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [reasons, setReasons] = useState({});

  const confirmMovement = () => {
    setLoading(true);
    if (movementType == 'collections' || movementType == 'imports') {
      GoodsIn.confirm(category, id, reasons)
        .then(() => {
          setLoading(false);
          refreshMovement();
          setModalOpen(false);
        })
        .catch(e => {
          setLoading(false);
          setError(e.errors || e.error);
        });
    } else {
      const driverNameParam = movementType == 'deliveries' ? driverName : null;
      const driverSignatureParam =
        movementType == 'deliveries' ? canvasRef.current.toDataURL() : null;
      const reasonsParam = movementType == 'deliveries' ? null : reasons;
      GoodsOut.confirm(category, id, driverNameParam, driverSignatureParam, reasonsParam)
        .then(() => {
          setLoading(false);
          refreshMovement();
          setModalOpen(false);
        })
        .catch(e => {
          setLoading(false);
          setError(e.errors || e.error);
        });
    }
  };

  const resetCanvas = () => {
    const ratio = window.devicePixelRatio;
    canvasRef.current.width = 650 * ratio;
    canvasRef.current.height = 200 * ratio;
    canvasRef.current.style.width = 650 + 'px';
    canvasRef.current.style.height = 200 + 'px';

    const context = canvasRef.current.getContext('2d');
    context.scale(ratio, ratio);
    context.lineCap = 'round';
    context.strokeStyle = 'black';
    context.lineWidth = 5;
    contextRef.current = context;
  };

  useEffect(() => {
    if (movementType == 'deliveries') resetCanvas();
  }, []);

  const startDrawing = ({ nativeEvent }) => {
    const { offsetX, offsetY } = nativeEvent;
    contextRef.current.beginPath();
    contextRef.current.moveTo(offsetX, offsetY);
    setIsDrawing(true);
  };

  const finishDrawing = () => {
    contextRef.current.closePath();
    setIsDrawing(false);
  };

  const draw = ({ nativeEvent }) => {
    if (isDrawing) {
      const { offsetX, offsetY } = nativeEvent;
      contextRef.current.lineTo(offsetX, offsetY);
      contextRef.current.stroke();
    } else {
      return;
    }
  };

  const missingAssignments = expectedAssignments?.filter(
    assignment => !packages?.some(pkg => pkg?.job?.id == assignment?.job?.id),
  );

  const unexpectedPackages = packages?.filter(pkg =>
    expectedAssignments?.every(assignment => assignment?.job?.id != pkg?.job?.id),
  );
  const unexpectedJobs = Object.groupBy(unexpectedPackages || [], pkg => pkg?.job?.id);

  return (
    <div className="confirm_movement_container">
      {loading && <LoadingSpinner />}
      {error && (
        <Error
          errorMessages={error}
          type={'error'}
          errorTitle={
            Array.isArray(error)
              ? `${error.length} error${error.length > 1 ? 's' : ''} occurred: `
              : ''
          }
        />
      )}
      {damaged && (
        <div className="damage_warning">
          <Error
            errorMessages={`Warning: This ${
              movementType == 'deliveries'
                ? 'delivery'
                : movementType.substring(0, movementType.length - 1)
            } contains one or more damaged items.`}
            type={'info'}
          />
        </div>
      )}
      {unexpectedJobs &&
        Object.keys(unexpectedJobs).map(job => {
          return (
            <div className="reasons_form" key={job}>
              <div className="label">
                <FontAwesomeIcon icon={faWarning} className="warning_icon" />
                Unexpected job - {unexpectedJobs[job][0]?.job?.jobReference}
              </div>
              <input
                data-testid="reasons_input"
                className="reasons_input"
                value={reasons[[job]]}
                placeholder="This job is included because..."
                onChange={e => setReasons({ ...reasons, [[job]]: e.target.value })}
              />
            </div>
          );
        })}
      {missingAssignments &&
        missingAssignments?.map(assignment => {
          return (
            <div className="reasons_form" key={assignment?.job?.id}>
              <div className="label">
                <FontAwesomeIcon icon={faWarning} className="warning_icon" />
                Missing job - {assignment?.job?.jobReference}
              </div>
              <input
                data-testid="reasons_input"
                className="reasons_input"
                value={reasons[assignment?.job?.id]}
                placeholder="This job is missing because..."
                onChange={e => setReasons({ ...reasons, [assignment?.job?.id]: e.target.value })}
              ></input>
            </div>
          );
        })}
      {movementType == 'deliveries' && (
        <div className="driver_form">
          <div className="driver_name_row">
            <div className="label">Driver Name</div>
            <input
              data-testid="driver_name_input"
              className="driver_name"
              value={driverName}
              onChange={e => setDriverName(e.target.value)}
            ></input>
          </div>
          <div className="driver_signature_row">
            <div className="label_and_reset_button">
              <div className="label">Signature</div>
              <button className="reset_button" onClick={() => resetCanvas()}>
                Reset
              </button>
            </div>
            <div className="driver_signature">
              <canvas
                data-testid="canvas_input"
                onMouseDown={startDrawing}
                onMouseUp={finishDrawing}
                onMouseMove={draw}
                onTouchStart={startDrawing}
                onTouchEnd={finishDrawing}
                onTouchMove={draw}
                onPointerDown={startDrawing}
                onPointerUp={finishDrawing}
                onPointerMoveCapture={draw}
                ref={canvasRef}
              ></canvas>
            </div>
          </div>
        </div>
      )}
      <div className="confirm_movement_buttons">
        <div className="message">
          <b>
            Mark this{' '}
            {movementType == 'deliveries'
              ? 'delivery'
              : movementType.substring(0, movementType.length - 1)}{' '}
            as completed?
          </b>
        </div>
        <button className="confirm_button" onClick={() => confirmMovement()}>
          Confirm
        </button>
      </div>
    </div>
  );
};

ConfirmModal.propTypes = {
  category: PropTypes.string,
  id: PropTypes.number,
  setModalOpen: PropTypes.func,
  refreshMovement: PropTypes.func,
  movementType: PropTypes.string,
  damaged: PropTypes.bool,
  packages: PropTypes.array,
  expectedAssignments: PropTypes.array,
};

export default ConfirmModal;
